import type { FormEvent } from 'react';
import React, { useCallback } from 'react';

import {
  AlineTransforms,
  isTableBorderSize,
  isTableElement,
  pxToIn,
  Queries,
  tableBorderSizes,
} from '@aline/common';
import type { Leaves, TableAlign, TableProps } from '@aline/common';
import { DialogActions, DialogContent, InputLabel, Typography } from '@mui/material';
import { Formik } from 'formik';
import { round } from 'lodash';
import { equals } from 'ramda';
import { Editor } from 'slate';
import { AlineButton, AlineDialog, FormikAlineInput } from 'src/components';
import { FormikBorderSelect } from 'src/components/formik/FormikBorderSelect';
import { FormikColorSelect } from 'src/components/formik/FormikColorSelect';
import { FormikOptionsSelect } from 'src/components/formik/FormikOptionsSelect';
import { useEditorTabStateSelector } from 'src/editor/editor-tab-state-registry';
import { useCurrentUserId } from 'src/gql';
import { useEditor } from 'src/layout-engine';
import { useEditingMode } from 'src/store/document/selectors';
import { makeStyles } from 'tss-react/mui';
import * as Yup from 'yup';

function roundToNearestTenth(num: number): number {
  return Math.round(num * 10) / 10;
}

const useStyles = makeStyles()(theme => ({
  bigLabel: {
    ...theme.appTypography.fieldLabel,
    fontSize: 16,
  },
  container: {
    paddingLeft: 8,
    paddingRight: 8,
    width: 412,
  },
  content: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  indentInput: {
    width: 100,
  },
  inputLabel: {
    ...theme.appTypography.fieldLabel,
    margin: theme.spacing(2, 0, 0.5, 0.2),
  },
  saveButton: {
    marginLeft: theme.spacing(1),
    width: 90,
  },
  selectInput: {
    '&:focus-within': {
      border: 'none',
    },
    border: 'none',
    width: 100,
  },
  selectRoot: {
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: 'rgba(0, 0, 0, 0.23)', // MUI's default border color
    },
  },
}));

interface TablePropsDialogProps {
  handleClose: () => void;
  open: boolean;
}

interface TablePropsForm {
  align: TableAlign;
  // needs to be a string so we can support `3.` state (w/decimal)
  indent: string;
  tableBorderWeight?: string | undefined;
  tableCellFillColor?: string | undefined;
  tableFillColor?: string | undefined;
}

const FormSchema = Yup.object<TablePropsForm>().shape({
  align: Yup.string()
    .typeError('Align must be a string')
    .matches(/^(left|center|right)$/, 'Align must be left, center or right'),
  /**
   * This needs to be a `string` because we need to support the input state with
   * a decimal without a trailing number like `1.` in the input field.
   */
  indent: Yup.string()
    .typeError('Left indent must be a string')
    .matches(/^-?\d+(?:\.\d*)?$/, 'Left indent must be a number'),
  tableBorderWeight: Yup.string()
    .typeError('Invalid table border weight')
    .oneOf(tableBorderSizes.map(String), 'Invalid table border weight'),
  tableCellFillColor: Yup.string().typeError('Invalid table cell fill color'),
  tableFillColor: Yup.string().typeError('Invalid table fill color'),
});

// How much the indent steppers goes up and down by
const indentStep = 0.1;
const alignKey: Leaves<TablePropsForm> = 'align';
const indentKey: Leaves<TablePropsForm> = 'indent';
const borderWeightKey: Leaves<TablePropsForm> = 'tableBorderWeight';
const tableFillColorKey: Leaves<TablePropsForm> = 'tableFillColor';
const tableCellFillColorKey: Leaves<TablePropsForm> = 'tableCellFillColor';

export function TablePropsDialog({ handleClose, open }: TablePropsDialogProps): JSX.Element | null {
  const editor = useEditor();
  const { classes } = useStyles();
  const currentUserId = useCurrentUserId();
  const { editingMode } = useEditingMode();

  const tableProperties = useEditorTabStateSelector(state => state.derived.tableProperties, equals);
  const tableCellProperties = useEditorTabStateSelector(
    state => state.derived.tableCellProperties,
    equals
  );

  /**
   * Find the maximum containerContentWidthIn
   */
  const selectedTableEntry = Editor.above(editor, { match: isTableElement });
  const maxIndentIn = selectedTableEntry
    ? pxToIn(
        Queries.getContainerContentWidthPx(editor, {
          at: selectedTableEntry[1],
        })
      )
    : Infinity;

  /**
   * Set initial form values
   */
  const initialValues = {
    align: tableProperties.align ?? 'left',
    indent: tableProperties.indent?.type === 'inch' ? tableProperties.indent.value.toString() : '0',
    tableBorderWeight: tableProperties.activeBorderWeight
      ? `${tableProperties.activeBorderWeight}`
      : undefined,
    tableCellFillColor: tableCellProperties.shade?.fillColor,
    tableFillColor: tableProperties.shade?.fillColor,
  };

  /**
   * Submit the form and update the table properties.
   */
  const onSubmit = useCallback(
    (values: TablePropsForm) => {
      if (!currentUserId || !editingMode) return;

      /**
       * Set some table props directly using `setTableProps`
       */
      const tableProps: Partial<TableProps> = {};

      if (values.align !== initialValues.align) {
        switch (values.align) {
          case 'left': {
            tableProps.align = 'left';
            tableProps.indent = {
              type: 'inch',
              value: round(
                Math.min(
                  maxIndentIn,
                  Math.max(0, isNaN(parseFloat(values.indent)) ? 0 : parseFloat(values.indent))
                ),
                2
              ),
            };
            break;
          }
          default: {
            tableProps.align = values.align;
            tableProps.indent = {
              type: 'nil',
            };
          }
        }
        tableProps.align = values.align;
      }
      if (values.indent !== initialValues.indent) {
        tableProps.indent = {
          type: 'inch',
          value: parseFloat(values.indent),
        };
      }
      if (values.tableFillColor !== initialValues.tableFillColor) {
        tableProps.shade = {
          fillColor: values.tableFillColor,
        };
      }

      if (Object.keys(tableProps).length > 0) {
        AlineTransforms.setTableProps(editor, {
          authorId: currentUserId,
          editingMode,
          props: tableProps,
        });
      }

      /**
       * Set table border weight
       */

      if (values.tableBorderWeight !== initialValues.tableBorderWeight) {
        const borderWeight = parseFloat(values.tableBorderWeight ?? '0');
        if (!isTableBorderSize(borderWeight)) {
          throw new Error('Invalid table border weight');
        }
        AlineTransforms.setSelectedTableCellsBorder(editor, {
          authorId: currentUserId,
          borderProps: {
            size: borderWeight,
            style: 'solid',
          },
          editingMode,
        });
      }

      /**
       * Set cell fill.
       */

      if (values.tableCellFillColor !== initialValues.tableCellFillColor) {
        AlineTransforms.setTableCellProps(editor, {
          authorId: currentUserId,
          editingMode,
          props: {
            shade: {
              fillColor: values.tableCellFillColor,
            },
          },
        });
      }

      handleClose();
    },
    [
      currentUserId,
      editingMode,
      editor,
      handleClose,
      maxIndentIn,
      initialValues.align,
      initialValues.indent,
      initialValues.tableBorderWeight,
      initialValues.tableCellFillColor,
      initialValues.tableFillColor,
    ]
  );

  return (
    <AlineDialog
      classes={{ paperWidthMd: classes.container }}
      open={open}
      title="Table properties"
      onClose={handleClose}
    >
      <Formik<TablePropsForm>
        enableReinitialize
        initialValues={initialValues}
        validationSchema={FormSchema}
        onSubmit={onSubmit}
      >
        {({
          dirty,
          handleReset,
          isSubmitting,
          isValid,
          setFieldValue,
          submitForm,
        }): JSX.Element => (
          <form
            onReset={handleReset}
            onSubmit={(event: FormEvent<HTMLFormElement>): void => {
              event.preventDefault();
              event.stopPropagation();
              submitForm();
            }}
          >
            <DialogContent className={classes.content}>
              <div>
                <Typography className={classes.bigLabel}>Fill Color</Typography>
                <InputLabel className={classes.inputLabel} htmlFor="rowCount">
                  Table Fill
                </InputLabel>
                <FormikColorSelect
                  fieldName={tableFillColorKey}
                  // If the user changes the table fill color, set the cell fill color accordingly
                  onSelectChange={color => setFieldValue(tableCellFillColorKey, color)}
                />
                <InputLabel className={classes.inputLabel} htmlFor="rowCount">
                  Cell Fill
                </InputLabel>
                <FormikColorSelect fieldName={tableCellFillColorKey} />
              </div>
              <div>
                <Typography className={classes.bigLabel}>Border</Typography>
                <InputLabel className={classes.inputLabel} htmlFor="rowCount">
                  Table Border
                </InputLabel>
                <FormikBorderSelect fieldName={borderWeightKey} />
              </div>
              <div>
                <Typography className={classes.bigLabel}>Position</Typography>
                <InputLabel className={classes.inputLabel} htmlFor="rowCount">
                  Table alignment
                </InputLabel>
                <FormikOptionsSelect<TableAlign>
                  className={classes.selectInput}
                  fieldName={alignKey}
                  options={[
                    { label: 'Left', value: 'left' },
                    { label: 'Center', value: 'center' },
                    { label: 'Right', value: 'right' },
                  ]}
                  selectClasses={{ root: classes.selectRoot }}
                  onSelectChange={option => {
                    setFieldValue(indentKey, 0);
                    setFieldValue(alignKey, option);
                  }}
                />
                <InputLabel className={classes.inputLabel} htmlFor="columnCount">
                  Indent (inches)
                </InputLabel>
                <FormikAlineInput
                  autoFocus
                  className={classes.indentInput}
                  fieldName={indentKey}
                  stepDown={val => {
                    const numVal =
                      typeof val !== 'string' ? 0 : isNaN(parseFloat(val)) ? 0 : parseFloat(val);
                    setFieldValue(alignKey, 'left');
                    setFieldValue(
                      indentKey,
                      roundToNearestTenth(Math.max(0, numVal - indentStep)).toString()
                    );
                  }}
                  stepUp={val => {
                    const numVal =
                      typeof val !== 'string' ? 0 : isNaN(parseFloat(val)) ? 0 : parseFloat(val);
                    setFieldValue(alignKey, 'left');
                    setFieldValue(
                      indentKey,
                      round(Math.min(maxIndentIn, numVal + indentStep), 2).toString()
                    );
                  }}
                  type="number"
                  onChange={e => {
                    setFieldValue(alignKey, 'left');
                    setFieldValue(indentKey, e.target.value);
                  }}
                />
              </div>
            </DialogContent>
            <DialogActions>
              <AlineButton color="primary" variant="outlined" onClick={handleClose}>
                Cancel
              </AlineButton>
              <AlineButton
                className={classes.saveButton}
                color="primary"
                disabled={!dirty || !isValid || isSubmitting}
                loading={isSubmitting}
                type="submit"
                variant="contained"
              >
                Ok
              </AlineButton>
            </DialogActions>
          </form>
        )}
      </Formik>
    </AlineDialog>
  );
}
